/**
 * Categories overview page — shows all 8 super-categories with their sub-categories.
 * Linked from "Alle ansehen" / "View all" on home dashboards.
 */
import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight,
  HeartPulse, Briefcase, Scale, Home, UserCircle, PiggyBank,
  GraduationCap, MoreHorizontal } from "lucide-react";
import { AuthContext } from "@/context/AppContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  HeartPulse, Briefcase, Scale, Home, UserCircle, PiggyBank,
  GraduationCap, MoreHorizontal,
};

const tr = (lang, dict) => dict[lang] || dict.en;

const CategoriesPage = ({ language = "en" }) => {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const user = authCtx?.user;
  const [supers, setSupers] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/super-categories`),
      axios.get(`${API}/categories`),
    ]).then(([s, c]) => {
      setSupers(s.data.super_categories || []);
      setCats(c.data || []);
    }).catch(() => {});
  }, []);

  const catName = (c) => c[`name_${language}`] || c.name_en || c.name;

  const handleCategoryClick = (catId) => {
    if (!user) {
      navigate(`/register?category=${catId}`);
    } else if (user.role === "provider") {
      navigate("/dashboard");
    } else {
      navigate(`/dashboard/jobs/new?category=${catId}`);
    }
  };

  return (
    <div data-testid="categories-page">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2"
            style={{ fontFamily: "Chivo, sans-serif" }}>
          {tr(language, {
            de: "Alle Kategorien",
            en: "All Categories",
            fr: "Toutes les catégories",
            it: "Tutte le categorie",
          })}
        </h1>
        <p className="text-blue-100/60 mb-8">
          {tr(language, {
            de: "Wählen Sie eine Kategorie, um den passenden Experten zu finden.",
            en: "Choose a category to find the right expert.",
            fr: "Choisissez une catégorie pour trouver l'expert qu'il vous faut.",
            it: "Scegli una categoria per trovare l'esperto giusto.",
          })}
        </p>

        <div className="space-y-8">
          {supers.map(s => {
            const Icon = ICONS[s.icon] || Briefcase;
            const memberCats = cats.filter(c => s.members.includes(c.id));
            return (
              <section key={s.id} data-testid={`section-${s.id}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#0044CC] to-[#1d4ed8] flex items-center justify-center">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
                      {s[`name_${language}`] || s.name || s.name_en}
                    </h2>
                    <p className="text-blue-100/50 text-xs">
                      {memberCats.length} {tr(language, { de: "Dienstleistungen", en: "services", fr: "services", it: "servizi" })}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {memberCats.map(c => (
                    <button
                      key={c.id}
                      onClick={() => handleCategoryClick(c.id)}
                      data-testid={`cat-${c.id}`}
                      className="group flex items-center justify-between bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-[#60a5fa]/40 rounded-2xl p-4 transition-all text-left"
                    >
                      <span className="text-white text-sm font-medium">{catName(c)}</span>
                      <ChevronRight className="w-4 h-4 text-blue-100/40 group-hover:text-[#60a5fa] transition-colors" />
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;
